import React, { useEffect, useMemo, useState } from "react";
import useUrlFilters from "hooks/useUrlFilters";
import {
  Card, Button, Space, Modal, Form, Input, InputNumber, Select, Switch,
  Tag, Dropdown, message, Divider, Descriptions, Empty, Typography,
} from "antd";
import Table from 'components/Common/Table';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, MoreOutlined, CopyOutlined,
} from "@ant-design/icons";

import {
  getManagedPlans, createPlan, updatePlan, deletePlan,
  getProducts, getGatewayPlans, errorMessage,
} from "./billingApi";

const { Text } = Typography;

const CYCLES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half-yearly" },
  { value: "yearly", label: "Yearly" },
];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const VALUE_TYPES = [
  { value: "int", label: "Integer" },
  { value: "bool", label: "Boolean" },
  { value: "str", label: "String" },
  { value: "unlimited", label: "Unlimited" },
];

const gatewayPlanLabel = (gp) => {
  const amount = gp.amount != null ? `${gp.currency} ${gp.amount}` : "—";
  return `${gp.provider} · ${gp.name || gp.provider_plan_id} · ${amount}`;
};

const URL_FILTERS = {
  product: { type: "string" },
};

const BillingPlans = () => {
  const [plans, setPlans] = useState([]);
  const [products, setProducts] = useState([]);
  const [gatewayPlans, setGatewayPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  // Product filter lives in the URL (client-side filter, ids kept as strings).
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const productFilter = filters.product || undefined;
  const setProductFilter = (value) => setFilters({ product: value });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const selectedGateway = Form.useWatch("gateway_plans", form);

  const gatewayPlanById = useMemo(() => {
    const m = {};
    gatewayPlans.forEach((g) => { m[g.id] = g; });
    return m;
  }, [gatewayPlans]);

  const load = async () => {
    setLoading(true);
    try {
      const [planRes, prodRes, gwRes] = await Promise.all([
        getManagedPlans(),
        getProducts(),
        getGatewayPlans(),
      ]);
      const planData = planRes.data?.results || planRes.data || [];
      const prodData = prodRes.data?.results || prodRes.data || [];
      const gwData = gwRes.data?.results || gwRes.data || [];
      setPlans(Array.isArray(planData) ? planData : []);
      setProducts(Array.isArray(prodData) ? prodData : []);
      setGatewayPlans(Array.isArray(gwData) ? gwData : []);
    } catch (err) {
      message.error(errorMessage(err, "Could not load plans."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredPlans = useMemo(
    () => (productFilter ? plans.filter((p) => String(p.product) === productFilter) : plans),
    [plans, productFilter],
  );

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ billing_cycle: "monthly", currency: "INR", trial_days: 0, is_active: true, sort_order: 0, features: [] });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      product: record.product,
      name: record.name,
      description: record.description,
      billing_cycle: record.billing_cycle,
      price: record.price,
      currency: record.currency,
      trial_days: record.trial_days,
      sort_order: record.sort_order,
      is_active: record.is_active,
      gateway_plans: (record.gateway_plans || []).map((g) => g.id),
      features: (record.features || []).map((f) => ({
        feature_key: f.feature_key, feature_value: f.feature_value, value_type: f.value_type,
      })),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = { ...values, features: values.features || [], gateway_plans: values.gateway_plans || [] };
      if (editing) {
        await updatePlan(editing.slug, payload);
        message.success("Plan updated");
      } else {
        await createPlan(payload);
        message.success("Plan created");
      }
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      load();
    } catch (err) {
      if (err?.errorFields) return; // form validation
      message.error(errorMessage(err, "Could not save plan."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await deletePlan(record.slug);
      message.success("Plan deleted");
      load();
    } catch (err) {
      message.error(errorMessage(err, "Could not delete plan."));
    }
  };

  const handleCopyId = (record) => {
    navigator.clipboard
      .writeText(String(record.id))
      .then(() => message.success("Plan ID copied to clipboard"))
      .catch(() => message.error("Could not copy Plan ID"));
  };

  const confirmDelete = (record) => {
    Modal.confirm({
      title: "Delete this plan?",
      content: record.name,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () => handleDelete(record),
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Plan ID", dataIndex: "id", key: "id",
      render: (v) => (
        <Typography.Text copyable={{ text: String(v) }} style={{ fontSize: 12 }}>{v}</Typography.Text>
      ),
    },
    { title: "Product", dataIndex: "product_name", key: "product_name", render: (v) => v || "—" },
    {
      title: "Price", key: "price",
      render: (_, r) => `${r.currency} ${parseFloat(r.price).toFixed(2)}`,
    },
    {
      title: "Cycle", dataIndex: "billing_cycle", key: "billing_cycle",
      render: (v) => CYCLES.find((c) => c.value === v)?.label || v,
    },
    { title: "Trial", dataIndex: "trial_days", key: "trial_days", render: (v) => (v > 0 ? `${v}d` : "—") },
    {
      title: "Gateway Plans", dataIndex: "gateway_plans", key: "gateway_plans",
      render: (gps) => (gps && gps.length
        ? <Space size={[0, 4]} wrap>{gps.map((g) => <Tag key={g.id} color="blue">{g.provider}</Tag>)}</Space>
        : <Tag>None</Tag>),
    },
    {
      title: "Active", dataIndex: "is_active", key: "is_active",
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Actions", key: "actions",
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          menu={{
            items: [
              { key: "copy", icon: <CopyOutlined />, label: "Copy Plan ID" },
              { key: "edit", icon: <EditOutlined />, label: "Edit" },
              { type: "divider" },
              { key: "delete", icon: <DeleteOutlined />, label: "Delete", danger: true },
            ],
            onClick: ({ key }) => {
              if (key === "copy") handleCopyId(record);
              else if (key === "edit") openEdit(record);
              else if (key === "delete") confirmDelete(record);
            },
          }}
        >
          <Button size="small" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const selectedGatewayDetails = (selectedGateway || [])
    .map((id) => gatewayPlanById[id])
    .filter(Boolean);

  return (
    <Card
      title="Plans"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Add Plan</Button>}
      bordered={false}
    >
      <div style={{ marginBottom: 12 }}>
        <Select
          placeholder="Filter by Product"
          allowClear
          style={{ width: 240 }}
          value={productFilter}
          onChange={setProductFilter}
          options={products.map((p) => ({ value: String(p.id), label: p.name }))}
        />
      </div>
      <Table
        dataSource={filteredPlans}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
      />

      <Modal
        title={editing ? "Edit Plan" : "Add Plan"}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => { setModalOpen(false); setEditing(null); form.resetFields(); }}
        confirmLoading={saving}
        width={720}
        okText={editing ? "Save" : "Create"}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="product" label="Product" rules={[{ required: true }]}>
            <Select placeholder="Select product" options={products.map((p) => ({ value: p.id, label: p.name }))} />
          </Form.Item>
          <Form.Item name="name" label="Plan Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Space size="large" style={{ display: "flex" }}>
            <Form.Item name="billing_cycle" label="Billing Cycle" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select options={CYCLES} />
            </Form.Item>
            <Form.Item name="price" label="Price" rules={[{ required: true }]} style={{ flex: 1 }}>
              <InputNumber min={0} precision={2} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="currency" label="Currency" style={{ flex: 1 }}>
              <Select options={CURRENCIES.map((c) => ({ value: c, label: c }))} />
            </Form.Item>
          </Space>
          <Space size="large" style={{ display: "flex" }}>
            <Form.Item name="trial_days" label="Trial Days" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="sort_order" label="Sort Order" style={{ flex: 1 }}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked" style={{ flex: 1 }}>
              <Switch />
            </Form.Item>
          </Space>

          <Divider orientation="left">Gateway Plans</Divider>
          <Form.Item
            name="gateway_plans"
            label="Select synced gateway plans"
            help="Gateway plans are synced from the provider (read-only). Select the ones that back this plan."
          >
            <Select
              mode="multiple"
              placeholder="Select gateway plans"
              optionFilterProp="label"
              options={gatewayPlans.map((g) => ({ value: g.id, label: gatewayPlanLabel(g) }))}
            />
          </Form.Item>
          {selectedGatewayDetails.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Selected gateway plan details:</Text>
              {selectedGatewayDetails.map((g) => (
                <Descriptions
                  key={g.id} size="small" bordered column={2}
                  style={{ marginTop: 8 }}
                  title={<Space><Tag color="blue">{g.provider}</Tag>{g.name || g.provider_plan_id}</Space>}
                >
                  <Descriptions.Item label="Provider Plan ID">{g.provider_plan_id || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Amount">{g.amount != null ? `${g.currency} ${g.amount}` : "—"}</Descriptions.Item>
                  <Descriptions.Item label="Period">{g.period || "—"}</Descriptions.Item>
                  <Descriptions.Item label="Interval">{g.interval ?? "—"}</Descriptions.Item>
                  <Descriptions.Item label="Sync Status">{g.sync_status}</Descriptions.Item>
                  <Descriptions.Item label="Last Synced">
                    {g.last_synced_at ? new Date(g.last_synced_at).toLocaleString() : "—"}
                  </Descriptions.Item>
                </Descriptions>
              ))}
            </div>
          )}

          <Divider orientation="left">Features</Divider>
          <Form.List name="features">
            {(fields, { add, remove }) => (
              <>
                {fields.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No features" />}
                {fields.map(({ key, name, ...rest }) => (
                  <Space key={key} align="baseline" style={{ display: "flex", marginBottom: 8 }}>
                    <Form.Item {...rest} name={[name, "feature_key"]} rules={[{ required: true, message: "Key" }]}>
                      <Input placeholder="key (e.g. users)" />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, "feature_value"]} rules={[{ required: true, message: "Value" }]}>
                      <Input placeholder="value (e.g. 10)" />
                    </Form.Item>
                    <Form.Item {...rest} name={[name, "value_type"]} initialValue="int">
                      <Select style={{ width: 130 }} options={VALUE_TYPES} />
                    </Form.Item>
                    <DeleteOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ value_type: "int" })} icon={<PlusOutlined />} block>
                  Add Feature
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </Card>
  );
};

export default BillingPlans;
